<template>
  <TimeSeries title="Combined Metrics Trends" :loading="loading">
    <template #graphic>
      <CombinedMetricsGraphic
        ref="graphic"
        :series="series"
        :chart-options="chartOptions"
        :is-hovered="isHovered"
        :zoom-enabled="zoomEnabled"
        @hover="handleHover"
        @enable-zoom="enableZoom"
      />
    </template>

    <template #analysis>
      <CombinedMetricsAnalysis
        :ai-insights="aiInsights"
        :ai-summary="aiSummary"
        :ai-insights-error="aiInsightsError"
        :ai-summary-loading="aiSummaryLoading"
        :detailed-analysis-loading="detailedAnalysisLoading"
        :flow-events="flowEvents"
        :zoom-enabled="zoomEnabled"
        @retry-analysis="retryAIAnalysis"
        @investigate-points="investigateAIPoints"
      />
    </template>
  </TimeSeries>
</template>

<script>
import TimeSeries from '../TimeSeries.vue'
import CombinedMetricsGraphic from './CombinedMetricsGraphic.vue'
import CombinedMetricsAnalysis from './CombinedMetricsAnalysis.vue'
import HTTP from '@/common/http'
import { mapGetters } from 'vuex'

export default {
  name: 'CombinedMetrics',
  components: {
    TimeSeries,
    CombinedMetricsGraphic,
    CombinedMetricsAnalysis
  },
  props: {
    id: {
      type: String,
      default: 'AllContracts'
    }
  },
  data () {
    return {
      series: [],
      rawSeriesData: {},
      loading: false,
      aiSummary: '',
      aiInsights: null,
      aiInsightsError: null,
      aiSummaryLoading: false,
      detailedAnalysisLoading: false,
      isHovered: false,
      zoomEnabled: false,
      retryCount: 0,
      maxRetries: 3,
      flowEvents: [],
      chartOptions: this.getDefaultChartOptions(),
      errorMessage: null
    }
  },
  computed: {
    ...mapGetters(['activeType'])
  },
  watch: {
    id: {
      immediate: true,
      handler: 'fetchData'
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    getDefaultChartOptions () {
      return {
        chart: {
          type: 'line',
          toolbar: { show: true },
          zoom: {
            enabled: false,
            type: 'x',
            autoScaleYaxis: true
          },
          events: {
            zoomed: async function (chartContext, { xaxis }) {
              const min = new Date(xaxis.min)
              await this.generateAISummary(min)
            }.bind(this)
          }
        },
        stroke: {
          width: [3, 0, 0]
        },
        plotOptions: {
          bar: {
            columnWidth: '50%'
          }
        },
        markers: {
          size: [0, 0, 5]
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            format: 'dd MMM yyyy'
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center'
        },
        xaxis: {
          type: 'datetime',
          title: { text: 'Date' },
          labels: { rotate: -45 }
        },
        yaxis: [
          {
            title: { text: 'Costs ($)' },
            labels: { formatter: val => `$${val.toFixed(2)}` },
            opposite: false
          },
          {
            title: { text: 'Deployments' },
            labels: { formatter: val => Math.round(val) },
            opposite: true
          },
          {
            title: { text: 'Alerts' },
            labels: { formatter: val => Math.round(val) },
            opposite: true
          }
        ],
        colors: ['#34D399', '#60A5FA', '#F87171']
      }
    },

    handleHover (isHovered) {
      this.isHovered = isHovered
    },

    enableZoom () {
      this.zoomEnabled = true
      this.chartOptions.chart.zoom.enabled = true
      this.$nextTick(() => {
        if (this.$refs.graphic && this.$refs.graphic.$refs.chart.updateOptions) {
          this.$refs.graphic.$refs.chart.updateOptions(this.chartOptions)
        }
      })
    },

    async fetchData () {
      this.loading = true
      try {
        const [costsRes, deploymentsRes, alertsRes] = await Promise.all([
          HTTP.get(`/costs/summary/${this.activeType}/${this.id}`),
          HTTP.get(`/ciinabox/metrics/${this.activeType}/${this.id}/month`),
          HTTP.get(`/alerts/metrics/${this.activeType}/${this.id}/month`)
        ])

        const formattedCosts = this.formatCostsData(costsRes.data.costs)
        const alertsData = this.formatAlertsData(alertsRes.data.metrics)
        const deploymentsData = this.formatDeploymentsData(deploymentsRes.data.metrics)

        this.series = [
          { name: 'Costs', type: 'line', data: formattedCosts },
          { name: 'Deployments', type: 'bar', data: deploymentsData },
          { name: 'Alerts', type: 'scatter', data: alertsData }
        ]

        this.rawSeriesData = {
          Costs: formattedCosts,
          Deployments: deploymentsData,
          Alerts: alertsData
        }

        await this.generateAISummary()
      } catch (error) {
        console.error('Error fetching data:', error)
        this.errorMessage = 'Failed to fetch data. Please try again later.'
      } finally {
        this.loading = false
      }
    },

    formatCostsData (costsData) {
      return Object.entries(costsData).flatMap(([env, months]) => {
        return Object.entries(months).map(([monthLabel, services]) => {
          const totalAmount = services.reduce((sum, service) => sum + (service.amount || 0), 0)
          const date = new Date(`${monthLabel} 1, ${new Date().getFullYear()}`)
          const now = new Date()
          if (date > now) date.setFullYear(now.getFullYear() - 1)
          return [date.getTime(), totalAmount]
        })
      }).sort((a, b) => a[0] - b[0])
    },

    formatAlertsData (alertsData) {
      return alertsData
        .filter(item => item.total > 0)
        .map(item => [new Date(item.date).getTime(), item.total])
    },

    formatDeploymentsData (deploymentsData) {
      return deploymentsData.map(item => [new Date(item.date).getTime(), item.count])
    },

    parseAIResponse (responseText) {
      if (!responseText || typeof responseText !== 'string') {
        return this.getDefaultInsights('Invalid response received')
      }
      const cleanedText = responseText.trim()
      const jsonMatch = cleanedText.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0])
          if (this.isValidInsightStructure(parsed)) {
            return parsed
          }
        } catch (e) {
          console.warn('JSON parsing failed, falling back to text parsing')
        }
      }
      return this.parseTextToStructuredInsights(cleanedText)
    },

    isValidInsightStructure (obj) {
      return obj &&
             typeof obj.title === 'string' &&
             typeof obj.summary === 'string' &&
             Array.isArray(obj.metrics) &&
             obj.metrics.length > 0
    },

    parseTextToStructuredInsights (text) {
      const insights = {
        title: this.extractTitle(text),
        summary: this.extractSummary(text),
        metrics: this.extractMetrics(text)
      }
      return insights
    },

    extractTitle (text) {
      const titlePatterns = [
        /(?:AI Insights?:?\s*)(.*?)(?:\n|$)/i,
        /(?:Analysis:?\s*)(.*?)(?:\n|$)/i,
        /(?:Summary:?\s*)(.*?)(?:\n|$)/i
      ]
      for (const pattern of titlePatterns) {
        const match = text.match(pattern)
        if (match && match[1].trim()) {
          return `AI Insights: ${match[1].trim()}`
        }
      }
      if (text.toLowerCase().includes('optimization') || text.toLowerCase().includes('improvement')) {
        return 'AI Insights: System Optimization Detected'
      } else if (text.toLowerCase().includes('stable') || text.toLowerCase().includes('reliable')) {
        return 'AI Insights: Excellent System Stability'
      } else if (text.toLowerCase().includes('alert') || text.toLowerCase().includes('issue')) {
        return 'AI Insights: System Analysis & Recommendations'
      }
      return 'AI Insights: Performance Analysis'
    },

    extractSummary (text) {
      const lines = text.split('\n').filter(line => line.trim())
      const contentLines = lines.filter(line => {
        const lower = line.toLowerCase()
        return !lower.startsWith('ai insights') &&
               !lower.startsWith('analysis:') &&
               !lower.startsWith('summary:') &&
               line.length > 20
      })
      const summary = contentLines.slice(0, 3).join(' ').substring(0, 300)
      return summary || text.substring(0, 200)
    },

    extractMetrics (text) {
      const metrics = []
      const metricPatterns = [
        {
          pattern: /(\d+)%.*(?:reduction|decrease|saved|lower).*cost/gi,
          createMetric: (match) => ({
            title: 'Cost Optimization',
            value: `${match[1]}% cost reduction`,
            description: 'through intelligent resource management'
          })
        },
        {
          pattern: /(\d+)%.*(?:increase|more|higher|improvement).*deployment/gi,
          createMetric: (match) => ({
            title: 'Deployment Velocity',
            value: `${match[1]}% deployment increase`,
            description: 'enhanced delivery pipeline efficiency'
          })
        },
        {
          pattern: /(zero|no|\d+).*alert/gi,
          createMetric: (match) => ({
            title: 'System Reliability',
            value: match[1].toLowerCase() === 'zero' || match[1].toLowerCase() === 'no'
              ? 'Zero alerts' : `${match[1]} alerts`,
            description: 'maintaining system stability'
          })
        },
        {
          pattern: /(\d+(?:\.\d+)?)\s*(?:dollars?|\$)/gi,
          createMetric: (match) => ({
            title: 'Cost Impact',
            value: `$${match[1]} monthly savings`,
            description: 'optimized resource utilization'
          })
        }
      ]
      metricPatterns.forEach(({ pattern, createMetric }) => {
        let match
        while ((match = pattern.exec(text)) !== null && metrics.length < 3) {
          metrics.push(createMetric(match))
        }
      })
      if (metrics.length === 0) {
        if (text.toLowerCase().includes('stable')) {
          metrics.push({
            title: 'System Health',
            value: 'Stable operation',
            description: 'consistent performance metrics'
          })
        }
        if (text.toLowerCase().includes('deployment')) {
          metrics.push({
            title: 'Deployment Status',
            value: 'Active deployments',
            description: 'continuous delivery pipeline'
          })
        }
        if (metrics.length === 0) {
          metrics.push({
            title: 'Analysis Complete',
            value: 'Data processed',
            description: 'comprehensive system review'
          })
        }
      }
      return metrics.slice(0, 3)
    },

    getDefaultInsights (errorMessage = 'Analysis unavailable') {
      return {
        title: 'AI Insights: System Analysis',
        summary: errorMessage,
        metrics: [{
          title: 'Status',
          value: 'Please refresh to retry',
          description: 'temporary analysis issue'
        }]
      }
    },

    async generateAISummary (selectedDate = null) {
      this.aiSummaryLoading = true
      this.aiInsights = null
      this.aiSummary = ''
      this.aiInsightsError = null

      try {
        // let year, month, day, endpointSuffix
        let year, endpointSuffix
        if (selectedDate) {
          // Zoomed: use timeSeriesSection
          year = selectedDate.getFullYear()
          // month = (selectedDate.getMonth() + 1).toString().padStart(2, '0')
          // day = selectedDate.getDate().toString().padStart(2, '0')
          endpointSuffix = 'timeSeriesSection'
        } else {
          // Unzoomed: use timeSeries with current date
          const today = new Date()
          year = today.getFullYear()
          // month = (today.getMonth() + 1).toString().padStart(2, '0')
          // day = today.getDate().toString().padStart(2, '0')
          endpointSuffix = 'timeSeries'
        }
        // const response = await HTTP.get(`exposure/widget/${this.id}/${year}/${month}/${day}/${endpointSuffix}`)
        const response = await HTTP.get(`exposure/widget/${this.id}/${year}/07/11/${endpointSuffix}`)
        const responseText = response.data && response.data.response ? response.data.response.trim() : ''

        if (!responseText) {
          throw new Error('Empty response received')
        }

        this.aiSummary = responseText
        this.aiInsights = this.parseAIResponse(responseText)

        this.retryCount = 0
      } catch (error) {
        console.error('AI insight error:', error)
        let errorMessage = 'Analysis temporarily unavailable'
        if (error.code === 'ECONNABORTED') {
          errorMessage = 'Analysis timed out, please try again'
        } else if (error.response && error.response.status >= 500) {
          errorMessage = 'Service temporarily unavailable'
        } else if (error.response && error.response.status === 429) {
          errorMessage = 'Too many requests, please wait a moment'
        }
        this.aiInsightsError = errorMessage
        this.aiInsights = null
      } finally {
        this.aiSummaryLoading = false
      }
    },

    retryAIAnalysis () {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++
        this.generateAISummary()
      } else {
        this.aiInsightsError = 'Maximum retry attempts reached. Please refresh the page.'
      }
    },

    async investigateAIPoints () {
      this.detailedAnalysisLoading = true
      try {
        if (!this.$refs.graphic || !this.$refs.graphic.$refs.chart || !this.$refs.graphic.$refs.chart.chart) return

        const globals = this.$refs.graphic.$refs.chart.chart.w.globals
        const xaxis = globals.minX && globals.maxX
          ? { min: globals.minX, max: globals.maxX }
          : null

        if (!xaxis) return

        const fromTs = xaxis.min

        const selectedDate = new Date(fromTs)
        let year = selectedDate.getFullYear()
        // let month = (selectedDate.getMonth() + 1).toString().padStart(2, '0')
        // let day = selectedDate.getDate().toString().padStart(2, '0')

        // For detailed (zoomed), use timeSeriesSection
        // const response = await HTTP.get(`exposure/widget/${this.id}/${year}/${month}/${day}/timeSeriesSection`)
        const response = await HTTP.get(`exposure/widget/${this.id}/${year}/07/11/timeSeriesSection`)
        const responseText = response.data.response.trim()

        const jsonMatch = responseText.match(/\[[\s\S]*\]/)
        const jsonText = jsonMatch ? jsonMatch[0] : responseText

        const responseJson = JSON.parse(jsonText)
        if (Array.isArray(responseJson)) {
          this.flowEvents = responseJson.map(event => ({
            ...event,
            id: this.generateUUID()
          }))
        } else {
          throw new Error('Response is not an array')
        }
      } catch (error) {
        console.error('Error generating detailed summary:', error)
        this.aiInsightsError = 'Failed to generate detailed analysis. Please try again.'
      } finally {
        this.detailedAnalysisLoading = false
      }
    },

    generateUUID () {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 8)
        return v.toString(16)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Cards/AICards/TimeSeriesCard/CombinedMetrics/CombinedMetrics.vue